import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  DEFAULT_UI_THEME,
  UI_THEME_PRESETS,
  UiThemePreset,
  UsuarioUiThemeInterface
} from '../../_interfaces/ui-theme.interface';
import { UiThemeService } from '../../_services/ui-theme/ui-theme.service';
import {
  DashboardChartColorTheme,
  DashboardChartThemeService
} from '../../report/dashboard/services/dashboard-chart-theme.service';
import { DashboardTableTheme } from '../../report/dashboard/services/dashboard-datatable.service';
import { DashboardTableThemeService } from '../../report/dashboard/services/dashboard-table-theme.service';

@Component({
  moduleId: module.id,
  selector: 'fixedplugin-cmp',
  templateUrl: 'fixedplugin.component.html',
  styleUrls: ['fixedplugin.component.css']
})
export class FixedPluginComponent implements OnInit, OnDestroy {
  dropdownOpen = false;
  themePresets = UI_THEME_PRESETS;
  theme: UsuarioUiThemeInterface;

  chartThemeOptions = this.chartThemeService.options;
  tableThemeOptions = this.tableThemeService.options;
  chartColorTheme: DashboardChartColorTheme = 'vibrant';
  tableTheme: DashboardTableTheme = 'paper';

  private subscriptions: Subscription[] = [];

  constructor(
    private uiThemeService: UiThemeService,
    private chartThemeService: DashboardChartThemeService,
    private tableThemeService: DashboardTableThemeService
  ) {
    this.theme = this.uiThemeService.getTheme();
    this.chartColorTheme = this.chartThemeService.getActiveTheme();
    this.tableTheme = this.tableThemeService.getActiveTheme();
  }

  ngOnInit(): void {
    if (!this.uiThemeService.isLoaded()) {
      this.uiThemeService.loadAndApply().then(theme => {
        this.theme = theme;
      });
    } else {
      this.theme = this.uiThemeService.getTheme();
    }

    this.subscriptions.push(
      this.chartThemeService.changed$.subscribe(theme => {
        this.chartColorTheme = theme;
      }),
      this.tableThemeService.changed$.subscribe(theme => {
        this.tableTheme = theme;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  toggleDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropdownOpen = !this.dropdownOpen;
  }

  isPresetActive(preset: UiThemePreset): boolean {
    const t = this.theme;
    const p = preset.theme;
    return (t.pageBackground || '').toLowerCase() === (p.pageBackground || '').toLowerCase()
      && (t.cardBackground || '').toLowerCase() === (p.cardBackground || '').toLowerCase()
      && (t.themeMode || 'light') === (p.themeMode || 'light')
      && (t.sidebarBackground || '') === (p.sidebarBackground || '');
  }

  applyPreset(preset: UiThemePreset, event: Event): void {
    event.stopPropagation();
    this.theme = { ...preset.theme };
    this.uiThemeService.updateTheme({ ...preset.theme });
  }

  onAccentColorInput(event: Event): void {
    const hex = (event.target as HTMLInputElement).value;
    this.applyAccentColor(hex, false);
  }

  onAccentColorChange(event: Event): void {
    const hex = (event.target as HTMLInputElement).value;
    this.applyAccentColor(hex, true);
  }

  onAccentHexBlur(event: Event): void {
    event.stopPropagation();
    const raw = (event.target as HTMLInputElement).value.trim();
    const hex = this.normalizeHex(raw);
    if (!hex) {
      (event.target as HTMLInputElement).value = this.theme.accentColor;
      return;
    }
    this.applyAccentColor(hex, true);
  }

  onChartThemeChange(theme: DashboardChartColorTheme): void {
    this.chartThemeService.setActiveTheme(theme);
  }

  onTableThemeChange(theme: DashboardTableTheme): void {
    this.tableThemeService.setActiveTheme(theme);
  }

  resetTheme(event: Event): void {
    event.stopPropagation();
    this.uiThemeService.resetTheme().then(theme => {
      this.theme = theme;
    });
    this.chartThemeService.setActiveTheme('vibrant');
    this.tableThemeService.setActiveTheme('paper');
  }

  private applyAccentColor(hex: string, persist: boolean): void {
    const normalized = this.normalizeHex(hex);
    if (!normalized) {
      return;
    }

    this.theme.accentColor = normalized;
    this.theme.tableHeaderBackground = 'accent';
    this.theme.tableHeaderText = '#ffffff';
    this.uiThemeService.updateTheme({
      accentColor: normalized,
      tableHeaderBackground: 'accent',
      tableHeaderText: '#ffffff'
    }, persist);
  }

  private normalizeHex(value: string): string | null {
    if (!value) {
      return null;
    }
    let v = value.trim();
    if (!v.startsWith('#')) {
      v = `#${v}`;
    }
    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
      return v.toLowerCase();
    }
    if (/^#[0-9a-fA-F]{3}$/.test(v)) {
      const r = v[1];
      const g = v[2];
      const b = v[3];
      return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
    }
    return null;
  }
}
